<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import type { GameType } from '../../global';
    import { GameTypeService } from '../../Services/GameTypeService';

    export let isOpen: boolean = false;
    export let gameType: GameType | null = null;

    const dispatch = createEventDispatcher();

    function closeModal(): void {
        dispatch('close');
    }

    function handleBackdropClick(event: MouseEvent): void {
        if (event.target === event.currentTarget) {
            closeModal();
        }
    }

    function handleKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            closeModal();
        }
    }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if isOpen && gameType}
    <div class="modal-backdrop" on:click={handleBackdropClick}>
        <div class="modal-content">
            <div class="modal-header">
                <h3 class="modal-title">{GameTypeService.getDisplayName(gameType)}</h3>
                <button class="close-button" on:click={closeModal}>&times;</button>
            </div>
            <div class="modal-body">
                {#if GameTypeService.getCardImage(gameType)}
                    <img 
                        src={GameTypeService.getCardImage(gameType)} 
                        alt={GameTypeService.getDisplayName(gameType)}
                        class="card-image"
                    />
                {:else}
                    <div class="placeholder">
                        <div class="placeholder-text">
                            {GameTypeService.getDisplayName(gameType)}
                        </div>
                    </div>
                {/if}
            </div>
        </div>
    </div>
{/if}

<style lang="scss">
    .modal-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.7);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
        backdrop-filter: blur(2px);
    }

    .modal-content {
        background: white;
        border-radius: 12px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        max-width: 90vw;
        max-height: 90vh;
        overflow: hidden;
        animation: modalSlideIn 0.3s ease-out;
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid #e0e0e0;
        background: #f8f9fa;
    }

    .modal-title {
        margin: 0;
        font-size: 1.2rem;
        font-weight: bold;
        text-transform: uppercase;
        color: #333;
    }

    .close-button {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        color: #666;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: background-color 0.2s;

        &:hover {
            background-color: #e0e0e0;
            color: #333;
        }
    }

    .modal-body {
        padding: 1.5rem;
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 300px;
    }

    .card-image {
        max-width: 100%;
        max-height: 400px;
        object-fit: contain;
        border-radius: 8px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    .placeholder {
        width: 300px;
        height: 200px;
        display: flex;
        justify-content: center;
        align-items: center;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border-radius: 12px;
        color: white;
        text-align: center;
    }

    .placeholder-text {
        font-size: 1.5rem;
        font-weight: bold;
        text-transform: uppercase;
    }

    @keyframes modalSlideIn {
        from {
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
        }
        to {
            opacity: 1;
            transform: scale(1) translateY(0);
        }
    }

    @media (max-width: 768px) {
        .modal-content {
            margin: 1rem;
            max-width: calc(100vw - 2rem);
        }

        .modal-header {
            padding: 0.75rem 1rem;
        }

        .modal-title {
            font-size: 1rem;
        }

        .modal-body {
            padding: 1rem;
            min-height: 250px;
        }

        .card-image {
            max-height: 300px;
        }

        .placeholder {
            width: 250px;
            height: 150px;
        }

        .placeholder-text {
            font-size: 1.2rem;
        }
    }
</style> 